/* eslint-disable react/jsx-fragments */
/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { Fragment } from 'react'
import { motion } from 'framer-motion'
import convert from 'htmr'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { AlertLevel, AlertType } from '../../../../generated/graphql'
import { DetailsPlanAlert } from '../../../../types'
import { errorIconColor, primaryTextColor, lightBlue } from '../../../../lib/styles/colors'
import { DangerBanner, ProgressBanner, SuccessBanner } from '../../../shared/alerts'
import { OutlineButton, SecondaryButton } from '../../../shared/buttons'

type AlertBannerProps = {
  /** The alert to display */
  alert: DetailsPlanAlert
  /** The text to display on the delete button */
  deleteButtonLabel?: string
  /** Whether to disable the clear button */
  disableClear?: boolean
  /** Whether to disable the delete button */
  disableDelete?: boolean
  /** Whether to disable the dismiss button */
  disableDismiss?: boolean
  /** Callback even when the Clear button is clicked */
  onClear?: () => void
  /** Callback even when the Delete button is clicked */
  onDelete?: () => void
  /** Callback even when the Dismiss button is clicked */
  onDismiss: () => void
  /** Whether to show the clear button */
  showClear?: boolean
  /** Whether to show the delete button */
  showDelete?: boolean
  /** Whether to show the dismiss button */
  showDismiss?: boolean
}

const defaultProps: Omit<AlertBannerProps, 'alert' | 'onDismiss'> = {
  deleteButtonLabel: 'Delete',
  disableClear: false,
  disableDelete: false,
  disableDismiss: false,
  onClear: undefined,
  onDelete: undefined,
  showClear: false,
  showDelete: true,
  showDismiss: true
}

const alertBanner = css`
  align-items: start;
  justify-content: unset;
  justify-items: start;

  .inner {
    width: 100%;
  }
`
const alertGridCss = css`
  margin-top: 6px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  width: 100%;
`
const alertActionsCss = css`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  margin-bottom: 10px;

  button {
    height: 34px;
    color: ${primaryTextColor};
    border-color: ${primaryTextColor};
  }
`

const AlertBanner = ({
  alert,
  deleteButtonLabel,
  disableClear,
  disableDelete,
  disableDismiss,
  onClear,
  onDelete,
  onDismiss,
  showClear,
  showDelete,
  showDismiss
}: AlertBannerProps) => {
  const { title, description, level, alertType } = alert
  const gridColumns = css`
    grid-template-columns: ${!showDelete || !showDismiss ? '5fr 1fr' : '2fr 1fr'};
  `
  const innerBanner = () => (
    <Fragment>
      <strong>{title}</strong>
      <div css={[alertGridCss, gridColumns]}>
        <div>{convert(description)}</div>
        <div css={alertActionsCss}>
          {showClear && (
            <OutlineButton disabled={disableClear} onClick={onClear}>
              Clear
            </OutlineButton>
          )}
          {showDelete && (
            <OutlineButton disabled={disableDelete} onClick={onDelete}>
              {deleteButtonLabel}
            </OutlineButton>
          )}
          {showDismiss && (
            <SecondaryButton disabled={disableDismiss} onClick={onDismiss}>
              Dismiss
            </SecondaryButton>
          )}
        </div>
      </div>
    </Fragment>
  )

  const getBanner = () => {
    if (alertType === AlertType.InProgressCourse) {
      return (
        <ProgressBanner
          css={alertBanner}
          icon={<CheckCircleOutlineIcon fontSize="small" style={{ color: lightBlue }} />}
          innerClassName="inner"
        >
          {innerBanner()}
        </ProgressBanner>
      )
    }
    if (alertType === AlertType.ItemDoesNotMeetRequirement) {
      return (
        <DangerBanner
          css={alertBanner}
          icon={<HighlightOffIcon />}
          iconColor={errorIconColor}
          innerClassName="inner"
        >
          {innerBanner()}
        </DangerBanner>
      )
    }
    if (level === AlertLevel.Success) {
      return (
        <SuccessBanner css={alertBanner} icon={<CheckCircle />} innerClassName="inner">
          {innerBanner()}
        </SuccessBanner>
      )
    }
    return (
      <DangerBanner
        css={alertBanner}
        icon={<ErrorIcon />}
        iconColor={errorIconColor}
        innerClassName="inner"
      >
        {innerBanner()}
      </DangerBanner>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.3 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, y: -100, transition: { duration: 0.3 } }}
    >
      {getBanner()}
    </motion.div>
  )
}

AlertBanner.defaultProps = defaultProps

export default AlertBanner
